import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pill, ShieldCheck } from "lucide-react";

/**
 * Minimal SEO-page layout used by orphan landing pages (state, vs, facts).
 *
 * Intentionally lightweight: we do NOT pull in the public Navbar or
 * Hamburger Menu because these pages must remain invisible to a normal
 * site visitor browsing the standard navigation. A search-engine or
 * AI-bot referral lands here directly and gets a polished but minimal
 * shell with a logo link home and a primary CTA.
 *
 * Per-page <title>, meta description, canonical, and JSON-LD are
 * injected via the `seo` prop and a single effect (CRA has no
 * react-helmet installed; we manage <head> nodes directly to keep
 * the dependency footprint flat).
 */
export default function SeoPageLayout({ seo = {}, children }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (seo.title) document.title = seo.title;

    const upsert = (selector, build) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(build.tag);
        document.head.appendChild(el);
      }
      Object.entries(build.attrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
      if (build.text !== undefined) el.textContent = build.text;
      return { el, created };
    };

    const tags = [];
    if (seo.description) {
      tags.push(upsert('meta[data-seo="page-desc"]', {
        tag: "meta",
        attrs: { name: "description", "data-seo": "page-desc", content: seo.description },
      }));
      tags.push(upsert('meta[data-seo="og-desc"]', {
        tag: "meta",
        attrs: { property: "og:description", "data-seo": "og-desc", content: seo.description },
      }));
      tags.push(upsert('meta[data-seo="tw-desc"]', {
        tag: "meta",
        attrs: { name: "twitter:description", "data-seo": "tw-desc", content: seo.description },
      }));
    }
    if (seo.title) {
      tags.push(upsert('meta[data-seo="og-title"]', {
        tag: "meta",
        attrs: { property: "og:title", "data-seo": "og-title", content: seo.title },
      }));
      tags.push(upsert('meta[data-seo="tw-title"]', {
        tag: "meta",
        attrs: { name: "twitter:title", "data-seo": "tw-title", content: seo.title },
      }));
    }
    if (seo.canonical) {
      tags.push(upsert('link[data-seo="canonical"]', {
        tag: "link",
        attrs: { rel: "canonical", "data-seo": "canonical", href: seo.canonical },
      }));
      tags.push(upsert('meta[data-seo="og-url"]', {
        tag: "meta",
        attrs: { property: "og:url", "data-seo": "og-url", content: seo.canonical },
      }));
    }
    if (seo.jsonLd) {
      const json = JSON.stringify(seo.jsonLd);
      tags.push(upsert('script[data-seo="page-jsonld"]', {
        tag: "script",
        attrs: { type: "application/ld+json", "data-seo": "page-jsonld" },
        text: json,
      }));
    }

    return () => {
      document.title = prevTitle;
      // Only remove tags we created on mount so we don't trash other
      // pages' SEO data when navigating quickly.
      tags.forEach(({ el, created }) => {
        if (created && el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [seo]);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="seo-page-layout">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="seo-home-link">
            <span className="w-8 h-8 rounded-full bg-purple-600 grid place-items-center">
              <Pill className="w-4 h-4 text-white" />
            </span>
            <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 transition"
            data-testid="seo-cta-get-started"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      <main>{children}</main>

      <footer className="mt-20 border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid sm:grid-cols-2 gap-6 text-sm text-slate-600">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="font-semibold text-slate-800">HIPAA-compliant telehealth</span>
            </div>
            <p className="leading-relaxed">
              GLPFlo Telehealth, Inc. provides physician-supervised, compounded
              GLP-1 therapy for medical weight management. No insurance billing,
              no controlled substances, adults only.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 sm:justify-end items-start">
            <Link to="/privacy-policy" className="hover:text-purple-700">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-purple-700">Terms</Link>
            <Link to="/shipping-study" className="hover:text-purple-700">Shipping study</Link>
            <Link to="/pricing" className="hover:text-purple-700">Pricing</Link>
            <Link to="/faq" className="hover:text-purple-700">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
